/**
 * The forms an urgent inspection produces — filled from the record, read and
 * amended by the operator, submitted to a named recipient, and kept.
 *
 * "Submit" does two things and says which: it SAVES an exact copy on the
 * inspection record (who sent it, to whom, when, a reference, a fingerprint
 * of the text, and whether the generated text was amended), and it hands the
 * text on — by e-mail where an address is given, as a file, or to the
 * clipboard. There is no direct line to a regulator's system or the NOTAM
 * system from here, and the panel says so rather than implying one.
 */
import { Fragment, useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import {
  FORMS,
  formReady,
  formText,
  RECIPIENTS,
  recordSubmission,
  sha256,
  type FormKey,
  type RecipientKey,
  type Submission,
} from "../../domain/urgentForms";
import { TRIGGERS, update, type UrgentInspection, type UrgentRegister } from "../../domain/urgentInspection";
import { OEMA_MOVEMENT_AREA } from "../../mock/urgentInspection";
import { Button, Card, EmptyState, Field, Input, Select } from "../../design/components";
import { x as ex } from "../executive/shared";
import { localStamp } from "../equipment/FleetEditors";
import { t } from "../../i18n";
import s from "./Urgent.module.css";
import { saveFile } from "../../lib/saveFile";

const formLabel = (k: FormKey) => FORMS.find((f) => f.key === k)!.label;
const toLabel = (k: RecipientKey) => RECIPIENTS.find((r) => r.key === k)!.label;

export function UrgentForms({ reg, onSave }: { reg: UrgentRegister; onSave: (next: UrgentRegister) => Promise<void> }) {
  const session = useSession();
  const list = useMemo(
    () => [...reg.inspections].sort((a, b) => b.openedAt.localeCompare(a.openedAt)),
    [reg.inspections],
  );
  const [inspId, setInspId] = useState<string>(() => list[0]?.id ?? "");
  const insp = list.find((i) => i.id === inspId) ?? list[0];
  const [form, setForm] = useState<FormKey>("snowtam");
  const [to, setTo] = useState<RecipientKey>("ais");
  const [address, setAddress] = useState("");
  const [edited, setEdited] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<Submission | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  if (!insp)
    return (
      <Card title="Forms" sub="filled from the inspection record">
        <EmptyState>{t("There is no inspection to fill a form from yet.")}</EmptyState>
      </Card>
    );

  const input = {
    insp,
    area: OEMA_MOVEMENT_AREA,
    site: session.site.code,
    now: new Date(),
    serial: reg.inspections.flatMap((i) => i.submissions ?? []).filter((x) => x.form === "snowtam").length + 1,
  };
  const generated = formText(form, input);
  const text = edited ?? generated;
  const ready = formReady(form, input);

  const pick = (k: FormKey) => {
    setForm(k);
    setEdited(null);
    setSent(null);
    setSaid(null);
    setTo(k === "snowtam" || k === "ficon" ? "ais" : k === "part139" ? "regulator" : "management");
  };

  const submit = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const at = new Date().toISOString();
      const next = recordSubmission(
        insp,
        {
          form,
          to,
          address: address.trim() || undefined,
          at,
          by: session.principal.name,
          content: text,
          amended: text !== generated,
          sha256: await sha256(text),
        },
        session.site.code,
        () => `sub-${crypto.randomUUID().slice(0, 8)}`,
      );
      await onSave(update(reg, insp.id, () => next));
      setSent(next.submissions!.at(-1)!);
    } finally {
      setBusy(false);
    }
  };

  const mailto = (sub: Submission) =>
    `mailto:${encodeURIComponent(sub.address ?? "")}?subject=${encodeURIComponent(
      `${sub.ref} — ${formLabel(sub.form)} — ${session.site.code}`,
    )}&body=${encodeURIComponent(sub.content)}`;

  const download = (sub: Submission) => {
    try {
      void saveFile(`${sub.ref}-${sub.form}.txt`, sub.content, "text/plain;charset=utf-8");
    } catch {
      setSaid(t("This browser would not save the file."));
    }
  };

  const copy = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setSaid(t("Copied to the clipboard."));
    } catch {
      setSaid(t("The clipboard is not available here; select the text and copy it."));
    }
  };

  const trig = (i: UrgentInspection) => t(TRIGGERS.find((x) => x.key === i.trigger)!.label);

  return (
    <Card
      title="Forms"
      sub="Drafts from the inspection record. Check, then submit."
    >
      <div data-testid="urgent-forms">
        <div className={ex.row}>
          <Field label="Inspection">
            <Select
              value={insp.id}
              onChange={(e) => {
                setInspId(e.target.value);
                setEdited(null);
                setSent(null);
              }}
              aria-label={t("Inspection")}
            >
              {list.map((i) => (
                <option key={i.id} value={i.id}>
                  {`${localStamp(i.openedAt)} · ${trig(i)}${i.closed ? "" : ` · ${t("open")}`}`}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className={s.formTabs} role="tablist" aria-label={t("Form")}>
          {FORMS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={form === f.key}
              className={form === f.key ? s.formTabOn : s.formTab}
              onClick={() => pick(f.key)}
              data-testid={`form-${f.key}`}
            >
              <span className={s.formAuth}>{t(f.authority)}</span>
              <b>{t(f.label)}</b>
            </button>
          ))}
        </div>
        <p className={ex.muted}>{t(FORMS.find((f) => f.key === form)!.what)}</p>

        {!ready && (
          <p className={s.err} role="alert">
            {t("No runway has all three thirds assessed yet, so there is no runway condition to report.")}
          </p>
        )}

        <textarea
          className={s.formText}
          value={text}
          onChange={(e) => setEdited(e.target.value)}
          rows={Math.min(28, text.split("\n").length + 1)}
          spellCheck={false}
          aria-label={t("Form text")}
          data-testid="form-text"
        />
        {edited !== null && edited !== generated && (
          <p className={ex.muted}>
            {t("Amended from the generated text; the saved copy will say so.")}{" "}
            <button type="button" className={s.linkBtn} onClick={() => setEdited(null)}>
              {t("Back to the generated text")}
            </button>
          </p>
        )}

        <div className={ex.row}>
          <Field label="Send to">
            <Select value={to} onChange={(e) => setTo(e.target.value as RecipientKey)} aria-label={t("Send to")}>
              {RECIPIENTS.map((r) => (
                <option key={r.key} value={r.key}>
                  {t(r.label)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="E-mail address (optional)">
            <Input
              type="email"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="form-address"
            />
          </Field>
          <Button variant="primary" onClick={() => void submit()} disabled={busy || !ready} data-testid="form-submit">
            {busy ? t("Saving…") : t("Submit and save a copy")}
          </Button>
        </div>

        {sent && (
          <div className={s.sent} data-testid="form-sent">
            <b>{t("Saved as {ref}.", { ref: sent.ref })}</b> {t("Now send it:")}{" "}
            {sent.address && (
              <a href={mailto(sent)} className={ex.tag}>
                {t("Open in e-mail")}
              </a>
            )}{" "}
            <button type="button" className={ex.tag} onClick={() => download(sent)}>
              {t("Save as a file")}
            </button>{" "}
            <button type="button" className={ex.tag} onClick={() => void copy(sent.content)}>
              {t("Copy the text")}
            </button>
          </div>
        )}
        {said && <p className={ex.muted}>{said}</p>}
        <p className={s.small}>
          {t(
            "Submitting saves a copy to the inspection record. It doesn't send anything. Send it by email, file, or your usual channel.",
          )}
        </p>

        <div className={ex.colTitle}>{t("Copies saved from this inspection")}</div>
        {(insp.submissions ?? []).length === 0 ? (
          <EmptyState>{t("Nothing has been submitted from this inspection yet.")}</EmptyState>
        ) : (
          <div className={ex.tableWrap}>
            <table className={ex.table} data-testid="form-copies">
              <tbody>
                {[...(insp.submissions ?? [])].reverse().map((c) => (
                  <Fragment key={c.id}>
                    <tr onClick={() => setViewing(viewing === c.id ? null : c.id)} style={{ cursor: "pointer" }}>
                      <td className={s.when}>
                        {localStamp(c.at)}
                        <div className={ex.muted}>{c.by}</div>
                      </td>
                      <td>
                        <b>{c.ref}</b>
                        <div>{t(formLabel(c.form))}</div>
                      </td>
                      <td>
                        {t(toLabel(c.to))}
                        {c.address && <div className={ex.muted}>{c.address}</div>}
                      </td>
                      <td>
                        {c.amended && <span className={ex.tag}>{t("amended")}</span>}
                        {c.sha256 && <div className={s.hash}>{`SHA-256 ${c.sha256.slice(0, 16)}…`}</div>}
                      </td>
                    </tr>
                    {viewing === c.id && (
                      <tr>
                        <td colSpan={4}>
                          <pre className={s.copy}>{c.content}</pre>
                          <button type="button" className={ex.tag} onClick={() => download(c)}>
                            {t("Save as a file")}
                          </button>{" "}
                          <button type="button" className={ex.tag} onClick={() => void copy(c.content)}>
                            {t("Copy the text")}
                          </button>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
